import React from 'react';
import { aoInspectieOverspanningen } from '../../../../../common/src/domain/customInspectionTypes/amsterdamOverspanningen/inspectieOverspanningen/aoInspectieOverspanningen';
import { ElementGroupNames } from '../../../../../common/src/enums/ElementGroupNames';
import { DataPanelRow, PanelBody, PanelButtonLink, PanelHeading, PanelWithoutForm } from '../../../components/elements';
import { FormatCondition } from '../../../components/Objects/FormatCondition';
import { AssetsViewer } from '../../../components/Surveys/Common/AssetsViewer';
import { useReactAsync } from '../../../components/utils/useReactAsync';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchGet } from '../../../store/openApiFetch';
import { Nen3140ScoreLabel } from '../../nen3140/Nen3140ScoreLabel';
import {
    BreakDownOpenState,
    SurveyDetailsElementGroupReportPanel,
    SurveyDetailsElementPanel,
    SurveyDetailsUnitPanel,
    SurveyDetailsUnitPanelDetails,
} from '../../verificatieBerekening/surveyDetailsElements';

async function loadElements({ surveyId }: { surveyId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/elements/amsterdamOverspanningen/inspectieOverspanningen/report', { surveyId });
    return result;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function SurveyDetailsAoInspectieOverspanningen(props: { surveyId: string }) {
    const translate = useTranslate();
    const { data } = useReactAsync(loadElements, { surveyId: props.surveyId }, []);
    const [breakDownOpenState, setBreakDownOpenState] = React.useState<BreakDownOpenState>({});

    function expandAll() {
        const expandedBreakDownOpenState = {} as { [key: string]: boolean };
        expandedBreakDownOpenState[ElementGroupNames.Superstructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Substructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Foundation] = true;

        for (const element of data!) {
            expandedBreakDownOpenState[element.id] = true;
            for (const unit of element.units) {
                expandedBreakDownOpenState[unit.id] = true;
                for (const manifestation of unit.manifestations) {
                    expandedBreakDownOpenState[manifestation.id] = true;
                }
            }
        }
        setBreakDownOpenState(expandedBreakDownOpenState);
    }

    function collapseAll() {
        setBreakDownOpenState({});
    }

    function onChangeBreakdownOpenState(key: string) {
        breakDownOpenState[key] = !breakDownOpenState[key];
        setBreakDownOpenState({ ...breakDownOpenState });
    }

    if (!data) {
        return null;
    }

    return (
        <PanelWithoutForm id="Nen3140Report">
            <PanelHeading text="Inspectie overspanningen" icon="fa fa-bolt"></PanelHeading>
            <PanelBody whiteBackground={true}>
                <PanelButtonLink
                    onClick={() => expandAll()}
                    text={translate('survey.expandAllComposition')}
                    icon="fa fa-plus-square-o"
                    xsmall={true}
                    rightAllign={true}
                />
                <PanelButtonLink
                    onClick={() => collapseAll()}
                    text={translate('survey.collapseAllComposition')}
                    icon="fa fa-minus-square-o "
                    xsmall={true}
                    rightAllign={true}
                />
                <SurveyDetailsElementGroupReportPanel extraColumns={['Beoordeling']}>
                    {data.map((element: any) => (
                        <SurveyDetailsElementPanel
                            // eslint-disable-next-line react/jsx-key
                            extraColumns={[<Nen3140ScoreLabel nen3140Score={element.nen3140Score} />]}
                            key={element.id}
                            breakDownOpenState={breakDownOpenState}
                            onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                            element={element}
                        >
                            {element.units.map((unit: any) => (
                                <SurveyDetailsUnitPanel
                                    // eslint-disable-next-line react/jsx-key
                                    extraColumns={[
                                        <FormatCondition
                                            key={unit.id}
                                            condition={unit.surveyInspectionStandardData.conditionScore}
                                            inspectionStandardType={aoInspectieOverspanningen.inspectionStandardType}
                                        />,
                                    ]}
                                    key={unit.id}
                                    breakDownOpenState={breakDownOpenState}
                                    onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                                    unit={unit}
                                >
                                    <SurveyDetailsUnitPanelDetails>
                                        <DataPanelRow>
                                            <div className="p10">
                                                <AssetsViewer
                                                    assets={
                                                        unit?.surveyInspectionStandardData?.fotoBestanden?.map((b: any) => ({
                                                            ...b,
                                                            id: b.assetId,
                                                        })) ?? []
                                                    }
                                                    surveyId={props.surveyId}
                                                />
                                            </div>
                                        </DataPanelRow>
                                    </SurveyDetailsUnitPanelDetails>
                                </SurveyDetailsUnitPanel>
                            ))}
                        </SurveyDetailsElementPanel>
                    ))}
                </SurveyDetailsElementGroupReportPanel>
            </PanelBody>
        </PanelWithoutForm>
    );
}
